import { partStarts, voiceTime, type Replay, type VoiceTrack } from "@69ly/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { voiceAudioUrl } from "../lib/demo-queries.ts";

/** Above this speed the voice is not played: it could not be understood. */
export const VOICE_MAX_SPEED = 2;
/** How far the voice may drift from the replay before it is put back in place. */
const DRIFT_SECONDS = 0.35;

/** How each track is heard, kept per map in this browser. */
export interface VoiceMix {
  /** Voice on or off for the whole map. */
  on: boolean;
  volume: Record<string, number>;
  muted: Record<string, boolean>;
  /** The one track heard alone, if any. */
  solo: string | null;
}

const EMPTY_MIX: VoiceMix = { on: true, volume: {}, muted: {}, solo: null };
const mixKey = (demoId: string) => `69ly.voiceMix.${demoId}`;

export function useVoiceMix(demoId: string): [VoiceMix, (change: Partial<VoiceMix> | ((m: VoiceMix) => Partial<VoiceMix>)) => void] {
  const [mix, setMix] = useState<VoiceMix>(() => {
    try {
      return { ...EMPTY_MIX, ...(JSON.parse(localStorage.getItem(mixKey(demoId)) ?? "{}") as Partial<VoiceMix>) };
    } catch {
      return EMPTY_MIX;
    }
  });
  const update = useCallback(
    (change: Partial<VoiceMix> | ((m: VoiceMix) => Partial<VoiceMix>)) =>
      setMix((m) => {
        const next = { ...m, ...(typeof change === "function" ? change(m) : change) };
        try {
          localStorage.setItem(mixKey(demoId), JSON.stringify(next));
        } catch {
          // Private windows: the mix lasts for the visit.
        }
        return next;
      }),
    [demoId],
  );
  return [mix, update];
}

/** Whether a track is heard with this mix. */
export const audible = (mix: VoiceMix, id: string) => mix.on && !mix.muted[id] && (mix.solo === null || mix.solo === id);

/** "12:34.5", "1:02:03.5"; "−0:03.0" before the recording starts. */
export function audioClock(seconds: number): string {
  const sign = seconds < 0 ? "−" : "";
  const s = Math.abs(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = (s % 60).toFixed(1).padStart(4, "0");
  return h ? `${sign}${h}:${String(m).padStart(2, "0")}:${rest}` : `${sign}${m}:${rest}`;
}

/** "12:34", "12:34.5", "1:02:03", "754.2" → seconds; null when it is not a time. */
export function parseAudioClock(text: string): number | null {
  const parts = text.trim().replace(",", ".").split(":");
  if (parts.length > 3 || parts.some((p) => !/^\d+(\.\d+)?$/.test(p))) return null;
  const n = parts.map(Number);
  if (n.slice(1).some((x) => x >= 60)) return null;
  return n.reduce((total, x) => total * 60 + x, 0);
}

export interface VoicePlayback {
  /** Each track's length in seconds, once its file has said. */
  durations: Record<string, number>;
  /** Where each track is at this tick (it may be before its start or past its end). */
  at: (track: VoiceTrack) => number;
}

/**
 * Plays the voice tracks along the replay: each one at the second of its audio
 * that goes with the tick on screen, at the replay's speed. A track drifting
 * more than a third of a second (a seek, a new round, a slow network) is put
 * back in place.
 */
export function useVoicePlayback({
  demoId,
  tracks,
  mix,
  replay,
  tick,
  playing,
  speed,
}: {
  demoId: string;
  tracks: VoiceTrack[];
  mix: VoiceMix;
  replay: Replay;
  tick: number;
  playing: boolean;
  speed: number;
}): VoicePlayback {
  const elements = useRef(new Map<string, HTMLAudioElement>());
  const [durations, setDurations] = useState<Record<string, number>>({});
  const starts = partStarts(replay);

  // One audio element per track, made and dropped with the list.
  useEffect(() => {
    const map = elements.current;
    for (const track of tracks) {
      if (map.has(track.id)) continue;
      const el = new Audio();
      el.preload = "metadata";
      el.addEventListener("loadedmetadata", () => {
        if (Number.isFinite(el.duration)) setDurations((d) => ({ ...d, [track.id]: el.duration }));
      });
      el.src = voiceAudioUrl(demoId, track.id);
      map.set(track.id, el);
    }
    for (const [id, el] of map) {
      if (tracks.some((x) => x.id === id)) continue;
      el.pause();
      el.removeAttribute("src");
      el.load();
      map.delete(id);
    }
  }, [demoId, tracks]);
  useEffect(() => {
    const map = elements.current;
    return () => {
      for (const el of map.values()) {
        el.pause();
        el.removeAttribute("src");
      }
      map.clear();
    };
  }, []);

  const at = useCallback((track: VoiceTrack) => voiceTime(track.offsets, starts, replay.tickRate, tick), [starts, replay.tickRate, tick]);

  useEffect(() => {
    for (const track of tracks) {
      const el = elements.current.get(track.id);
      if (!el) continue;
      el.volume = Math.max(0, Math.min(1, mix.volume[track.id] ?? 1));
      const target = at(track);
      const inside = target >= 0 && (!Number.isFinite(el.duration) || target < el.duration - 0.05);
      if (playing && speed <= VOICE_MAX_SPEED && inside && audible(mix, track.id)) {
        if (el.playbackRate !== speed) el.playbackRate = speed;
        if (el.paused) {
          el.currentTime = target;
          void el.play().catch(() => undefined);
        } else if (!el.seeking && Math.abs(el.currentTime - target) > DRIFT_SECONDS) {
          el.currentTime = target;
        }
      } else if (!el.paused) el.pause();
    }
  }, [tracks, mix, at, playing, speed]);

  return { durations, at };
}
